import React, { useMemo, useState } from 'react';
import {
    Accordion, AccordionDetails, AccordionSummary, Box, Button, Chip, Dialog, DialogActions, DialogContent,
    DialogTitle, IconButton, LinearProgress, Paper, Stack, Table, TableBody, TableCell, TableContainer, TableHead,
    TableRow, Tooltip, Typography,
} from '@mui/material';
import {
    Add as AddIcon, Delete as DeleteIcon, Edit as EditIcon, ExpandMore as ExpandMoreIcon,
    History as HistoryIcon, PlaylistAdd as PlaylistAddIcon, RateReview as RateReviewIcon,
} from '@mui/icons-material';
import { ActivityNode, ExecutionOverview, Workstream } from '../../../types/execution';
import executionService from '../../../services/executionService';
import { GanttBar, GanttHeader } from './GanttBar';
import { StatusChip } from './StatusChip';
import { ActivityDialog, ActivityUpdateDialog, BaselineDialog, WorkstreamDialog } from './ScheduleDialogs';
import {
    TimelineRange, buildTimeline, errorMessage, flattenActivities, formatPct, formatShortDate, timelineFromOverview,
} from './executionUtils';

type Notify = (message: string, severity?: 'success' | 'error' | 'info' | 'warning') => void;

interface ScheduleViewProps {
    projectId: number;
    overview: ExecutionOverview;
    reload: () => void;
    notify: Notify;
}

interface ActivityTableProps {
    workstream: Workstream;
    range: TimelineRange;
    referenceDate: string;
    readOnly?: boolean;
    onEdit?: (activity: ActivityNode) => void;
    onAddChild?: (activity: ActivityNode) => void;
    onUpdate?: (activity: ActivityNode) => void;
    onDelete?: (activity: ActivityNode) => void;
}

export const ActivityTable: React.FC<ActivityTableProps> = ({
    workstream, range, referenceDate, readOnly, onEdit, onAddChild, onUpdate, onDelete,
}) => {
    const rows = flattenActivities(workstream.phases);
    return (
        <TableContainer component={Paper} variant="outlined">
            <Table size="small">
                <TableHead>
                    <TableRow>
                        <TableCell sx={{ minWidth: 240 }}>Atividade</TableCell>
                        <TableCell align="center">% Real</TableCell>
                        <TableCell align="center">% Esperado</TableCell>
                        <TableCell>Estado</TableCell>
                        <TableCell>Responsável</TableCell>
                        <TableCell>Início</TableCell>
                        <TableCell>Fim</TableCell>
                        <TableCell sx={{ minWidth: 300 }}><GanttHeader range={range} /></TableCell>
                        <TableCell>Riscos</TableCell>
                        {!readOnly && <TableCell align="right" sx={{ minWidth: 150 }}>Ações</TableCell>}
                    </TableRow>
                </TableHead>
                <TableBody>
                    {rows.map((activity) => {
                        const isPhase = activity.depth === 0;
                        const gap = activity.progress_expected !== null ? activity.progress_expected - activity.progress_real : 0;
                        return (
                            <TableRow key={activity.id} sx={{ bgcolor: isPhase ? 'action.hover' : undefined }}>
                                <TableCell sx={{ pl: 2 + activity.depth * 3, fontWeight: isPhase ? 600 : 400 }}>
                                    {activity.name}
                                    {!!activity.tasks?.total && (
                                        <Tooltip title={`${activity.tasks.total} tarefa(s) vinculada(s): ${activity.tasks.open} aberta(s), ${activity.tasks.late} atrasada(s)`}>
                                            <Chip size="small" variant="outlined" sx={{ ml: 1, height: 20 }}
                                                color={activity.tasks.late ? 'error' : activity.tasks.open ? 'primary' : 'success'}
                                                label={activity.tasks.open ? `${activity.tasks.open} tarefa(s) aberta(s)` : 'tarefas concluídas'} />
                                        </Tooltip>
                                    )}
                                    {activity.update && (activity.update.highlights || activity.update.next_steps) && (
                                        <Tooltip
                                            title={(
                                                <Box sx={{ whiteSpace: 'pre-line' }}>
                                                    <b>{formatShortDate(activity.update.report_date)}</b>
                                                    {activity.update.highlights && `\n${activity.update.highlights}`}
                                                    {activity.update.next_steps && `\nPróximos: ${activity.update.next_steps}`}
                                                </Box>
                                            )}
                                        >
                                            <RateReviewIcon fontSize="inherit" color="action" sx={{ ml: 1, verticalAlign: 'middle' }} />
                                        </Tooltip>
                                    )}
                                </TableCell>
                                <TableCell align="center" sx={{ minWidth: 90 }}>
                                    <Typography variant="body2" fontWeight={isPhase ? 600 : 400}>{formatPct(activity.progress_real)}</Typography>
                                    <LinearProgress variant="determinate" value={Math.min(100, activity.progress_real)} sx={{ height: 4, borderRadius: 2 }} />
                                </TableCell>
                                <TableCell align="center">
                                    <Typography variant="body2" color={gap >= 10 ? 'error' : 'text.primary'}>
                                        {formatPct(activity.progress_expected)}
                                    </Typography>
                                </TableCell>
                                <TableCell><StatusChip status={activity.status} /></TableCell>
                                <TableCell sx={{ whiteSpace: 'nowrap' }}>{activity.responsible || '-'}</TableCell>
                                <TableCell>{formatShortDate(activity.planned_start)}</TableCell>
                                <TableCell>{formatShortDate(activity.planned_end)}</TableCell>
                                <TableCell>
                                    <GanttBar
                                        range={range}
                                        plannedStart={activity.planned_start}
                                        plannedEnd={activity.planned_end}
                                        progress={activity.progress_real}
                                        status={activity.status}
                                        referenceDate={referenceDate}
                                        label={activity.milestone_label}
                                        bold={isPhase}
                                    />
                                </TableCell>
                                <TableCell>
                                    <Stack direction="row" spacing={0.5}>
                                        {activity.risk_codes.map((code) => <Chip key={code} size="small" label={code} color="warning" variant="outlined" />)}
                                    </Stack>
                                </TableCell>
                                {!readOnly && (
                                    <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                                        <Tooltip title="Atualização semanal">
                                            <IconButton size="small" color="primary" onClick={() => onUpdate?.(activity)}><RateReviewIcon fontSize="small" /></IconButton>
                                        </Tooltip>
                                        <Tooltip title="Adicionar sub-atividade">
                                            <IconButton size="small" onClick={() => onAddChild?.(activity)}><PlaylistAddIcon fontSize="small" /></IconButton>
                                        </Tooltip>
                                        <Tooltip title="Editar">
                                            <IconButton size="small" onClick={() => onEdit?.(activity)}><EditIcon fontSize="small" /></IconButton>
                                        </Tooltip>
                                        <Tooltip title="Excluir">
                                            <IconButton size="small" color="error" onClick={() => onDelete?.(activity)}><DeleteIcon fontSize="small" /></IconButton>
                                        </Tooltip>
                                    </TableCell>
                                )}
                            </TableRow>
                        );
                    })}
                </TableBody>
            </Table>
        </TableContainer>
    );
};

const ScheduleView: React.FC<ScheduleViewProps> = ({ projectId, overview, reload, notify }) => {
    const range = useMemo(() => timelineFromOverview(overview), [overview]);
    const [expanded, setExpanded] = useState<number | false>(overview.workstreams[0]?.id ?? false);

    const [wsDialog, setWsDialog] = useState<{ open: boolean; workstream: Workstream | null }>({ open: false, workstream: null });
    const [activityDialog, setActivityDialog] = useState<{
        open: boolean; workstream: Workstream | null; activity: ActivityNode | null; parent: ActivityNode | null;
    }>({ open: false, workstream: null, activity: null, parent: null });
    const [updateActivity, setUpdateActivity] = useState<ActivityNode | null>(null);
    const [baselineWs, setBaselineWs] = useState<Workstream | null>(null);
    const [baselineView, setBaselineView] = useState<{ title: string; workstream: Workstream } | null>(null);
    const [confirm, setConfirm] = useState<{ message: string; action: () => Promise<void> } | null>(null);

    const allRisks = [...overview.risks, ...overview.issues];
    const findWorkstream = (id: number) => overview.workstreams.find((ws) => ws.id === id) || null;

    const handleViewBaseline = async (baselineId: number) => {
        try {
            const data = await executionService.getBaselineSchedule(baselineId, overview.report_date);
            setBaselineView({ title: `${data.workstream.name} — ${data.baseline.name}`, workstream: data.workstream });
        } catch (error) {
            notify(errorMessage(error, 'Erro ao carregar linha de base'), 'error');
        }
    };

    const runConfirm = async () => {
        if (!confirm) return;
        try {
            await confirm.action();
            reload();
        } catch (error) {
            notify(errorMessage(error, 'Erro ao excluir'), 'error');
        } finally {
            setConfirm(null);
        }
    };

    return (
        <Box>
            <Box display="flex" justifyContent="flex-end" mb={2}>
                <Button variant="contained" startIcon={<AddIcon />} onClick={() => setWsDialog({ open: true, workstream: null })}>
                    Nova frente
                </Button>
            </Box>

            {overview.workstreams.length === 0 && (
                <Typography color="text.secondary">
                    Cadastre as frentes do projeto (ex.: um sub-projeto por frente). Cada frente começa com as fases padrão,
                    às quais você adiciona as atividades com datas planejadas.
                </Typography>
            )}

            {overview.workstreams.map((ws) => (
                <Accordion key={ws.id} expanded={expanded === ws.id} onChange={(_, isOpen) => setExpanded(isOpen ? ws.id : false)} sx={{ mb: 1 }}>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Box display="flex" alignItems="center" gap={2} width="100%" pr={2} flexWrap="wrap">
                            <Typography variant="h6" sx={{ minWidth: 200 }}>{ws.name}</Typography>
                            <Typography variant="body2" color="text.secondary">Owner: {ws.owner_name || '-'}</Typography>
                            <Box sx={{ flexGrow: 1, minWidth: 160 }}>
                                <LinearProgress variant="determinate" value={Math.min(100, ws.progress_real)} />
                            </Box>
                            <Typography variant="body2">
                                Real {formatPct(ws.progress_real)} · Esperado {formatPct(ws.progress_expected)}
                            </Typography>
                            {ws.status_note && <Chip size="small" label={ws.status_note} />}
                        </Box>
                    </AccordionSummary>
                    <AccordionDetails>
                        <Stack direction="row" spacing={1} mb={2} flexWrap="wrap">
                            <Button size="small" startIcon={<AddIcon />}
                                onClick={() => setActivityDialog({ open: true, workstream: ws, activity: null, parent: null })}>
                                Nova fase
                            </Button>
                            <Button size="small" startIcon={<HistoryIcon />} onClick={() => setBaselineWs(ws)}>
                                Linhas de base ({ws.baselines.length})
                            </Button>
                            <Button size="small" startIcon={<EditIcon />} onClick={() => setWsDialog({ open: true, workstream: ws })}>
                                Editar frente
                            </Button>
                            <Button size="small" color="error" startIcon={<DeleteIcon />}
                                onClick={() => setConfirm({
                                    message: `Excluir a frente "${ws.name}" com todo o cronograma?`,
                                    action: () => executionService.deleteWorkstream(ws.id),
                                })}>
                                Excluir frente
                            </Button>
                        </Stack>
                        <ActivityTable
                            workstream={ws}
                            range={range}
                            referenceDate={overview.report_date}
                            onEdit={(activity) => setActivityDialog({ open: true, workstream: ws, activity, parent: null })}
                            onAddChild={(parent) => setActivityDialog({ open: true, workstream: ws, activity: null, parent })}
                            onUpdate={setUpdateActivity}
                            onDelete={(activity) => setConfirm({
                                message: `Excluir "${activity.name}"${activity.children.length ? ' e suas sub-atividades' : ''}?`,
                                action: () => executionService.deleteActivity(activity.id),
                            })}
                        />
                    </AccordionDetails>
                </Accordion>
            ))}

            <WorkstreamDialog
                open={wsDialog.open}
                projectId={projectId}
                workstream={wsDialog.workstream}
                onClose={() => setWsDialog({ open: false, workstream: null })}
                onSaved={reload}
                notify={notify}
            />
            <ActivityDialog
                members={overview.access.members}
                open={activityDialog.open}
                workstream={activityDialog.workstream}
                activity={activityDialog.activity}
                parent={activityDialog.parent}
                risks={allRisks}
                onClose={() => setActivityDialog({ open: false, workstream: null, activity: null, parent: null })}
                onSaved={reload}
                notify={notify}
            />
            <ActivityUpdateDialog
                open={!!updateActivity}
                activity={updateActivity}
                reportDate={overview.report_date}
                onClose={() => setUpdateActivity(null)}
                onSaved={reload}
                notify={notify}
            />
            <BaselineDialog
                open={!!baselineWs}
                workstream={baselineWs ? findWorkstream(baselineWs.id) : null}
                onClose={() => setBaselineWs(null)}
                onSaved={reload}
                onView={handleViewBaseline}
                notify={notify}
            />

            <Dialog open={!!baselineView} onClose={() => setBaselineView(null)} maxWidth="xl" fullWidth>
                <DialogTitle>{baselineView?.title}</DialogTitle>
                <DialogContent>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                        % esperado calculado com as datas desta linha de base; % real é o atual.
                    </Typography>
                    {baselineView && (
                        <ActivityTable
                            workstream={baselineView.workstream}
                            range={buildTimeline([baselineView.workstream], overview.report_date)}
                            referenceDate={overview.report_date}
                            readOnly
                        />
                    )}
                </DialogContent>
                <DialogActions><Button onClick={() => setBaselineView(null)}>Fechar</Button></DialogActions>
            </Dialog>

            <Dialog open={!!confirm} onClose={() => setConfirm(null)}>
                <DialogTitle>Confirmar exclusão</DialogTitle>
                <DialogContent><Typography>{confirm?.message}</Typography></DialogContent>
                <DialogActions>
                    <Button onClick={() => setConfirm(null)}>Cancelar</Button>
                    <Button color="error" onClick={runConfirm}>Excluir</Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default ScheduleView;
